/**
 * Timer calculation utilities
 *
 * Game-type aware timer functions for all round-based games.
 * Each game type has its own timer state interface and calculation function.
 *
 * Prefixes:
 * - Hide and Seek: "Hns"
 * - FP Race: "Fpr"
 */
import { GameType } from '../constants/gameTypes';
import { isRoundActive, isHideSeekSettings, isFprSettings } from '../types/round';
/**
 * Calculate the current timer state for a Hide and Seek round
 * This function is designed to be used on both server and client
 * to ensure consistent timer calculations.
 *
 * @param round - The round to calculate timer state for
 * @param now - The current timestamp (Date.now())
 * @returns The calculated timer state, or null if round is not active
 */
export function calculateHnsTimerState(startedAt, hidingDuration, roundDuration, now) {
    const elapsedSinceStart = Math.floor((now - startedAt) / 1000);
    if (elapsedSinceStart < hidingDuration) {
        return {
            gameType: GameType.HIDE_AND_SEEK,
            phase: 'hiding',
            secondsElapsed: elapsedSinceStart,
            secondsRemaining: hidingDuration - elapsedSinceStart,
            secondsMax: roundDuration,
            isTimeUp: false,
            isHidingCountdown: true,
            hidingSecondsRemaining: hidingDuration - elapsedSinceStart,
            roundSecondsElapsed: 0,
            roundSecondsMax: roundDuration,
            isRoundTimeUp: false,
        };
    }
    const roundSecondsElapsed = elapsedSinceStart - hidingDuration;
    const isRoundTimeUp = roundDuration > 0 && roundSecondsElapsed >= roundDuration;
    const secondsRemaining = roundDuration > 0 ? Math.max(0, roundDuration - roundSecondsElapsed) : 0;
    return {
        gameType: GameType.HIDE_AND_SEEK,
        phase: 'seeking',
        secondsElapsed: roundSecondsElapsed,
        secondsRemaining,
        secondsMax: roundDuration,
        isTimeUp: isRoundTimeUp,
        isHidingCountdown: false,
        hidingSecondsRemaining: 0,
        roundSecondsElapsed,
        roundSecondsMax: roundDuration,
        isRoundTimeUp,
    };
}
export function getHnsRoundTimerState(round, now) {
    if (!round || !isRoundActive(round) || !round.startedAt)
        return null;
    // Hide and Seek Teams rounds run the same hiding/seeking timers.
    if (!isHideSeekSettings(round.settings))
        return null;
    return calculateHnsTimerState(round.startedAt, round.settings.hidingDuration, round.settings.roundDuration, now);
}
/**
 * Calculate the current timer state for an FP Race round
 *
 * FPR races no longer have a time limit - they end when all teams finish
 * or when the admin ends the round. Timer just counts up from race start.
 *
 * @param round - The round to calculate timer state for
 * @param now - The current timestamp (Date.now())
 * @returns The calculated timer state, or null if round is not active
 */
export function calculateFprTimerState(startedAt, phase, now) {
    const elapsedMs = Math.max(0, now - startedAt);
    const elapsedSinceStart = Math.floor(elapsedMs / 1000);
    return {
        gameType: GameType.FP_RACE,
        phase,
        secondsElapsed: elapsedSinceStart,
        secondsRemaining: 0,
        secondsMax: 0,
        isTimeUp: false,
        elapsedMs,
    };
}
export function getFprRoundTimerState(round, now) {
    if (!round || !round.startedAt)
        return null;
    if (!isFprSettings(round.settings))
        return null;
    if (round.status !== 'countdown' && round.status !== 'racing')
        return null;
    return calculateFprTimerState(round.startedAt, round.status, now);
}
/**
 * Get the timer state for any round type (game-type aware)
 * Automatically dispatches to the correct game-specific function.
 *
 * @param round - The round to calculate timer state for
 * @param now - The current timestamp (Date.now())
 * @returns The calculated timer state, or null if round is not active
 */
export function getUnifiedRoundTimerState(round, now) {
    if (!round)
        return null;
    if (isHideSeekSettings(round.settings)) {
        return getHnsRoundTimerState(round, now);
    }
    if (isFprSettings(round.settings)) {
        return getFprRoundTimerState(round, now);
    }
    return null;
}
